import React, { useState, useMemo, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Container, Row, Col, Button, Badge, Offcanvas } from "react-bootstrap";
import { 
  FaDownload, FaExternalLinkAlt, FaInfoCircle, FaLayerGroup, 
  FaUserGraduate, FaClipboardCheck, FaPlusSquare, FaArrowLeft,
  FaChevronRight, FaFilter, FaGraduationCap
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

import Header from "../components/Header";
import Footer from "../components/Footer";
import programDetails from "../programsdetail";

const GraduateProgramDetails = () => {
  const { programId } = useParams();
  const [activeSection, setActiveSection] = useState("overview");
  const [showDrawer, setShowDrawer] = useState(false);
  const contentRef = useRef(null);

  const program = useMemo(() => programDetails[programId] || null, [programId]);

  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    if (contentRef.current) {
      const offset = 100;
      const elementPosition = contentRef.current.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  if (!program) {
    return (
      <div className="text-center py-5 vh-100 d-flex flex-column justify-content-center align-items-center">
        <h3 className="fw-900 text-navy">Graduate Program Not Found</h3>
        <Link to="/programs" className="btn btn-primary rounded-pill px-4 mt-3">Back to Catalog</Link>
      </div>
    );
  }

  const navItems = [
    { id: "overview", label: "Overview", icon: <FaInfoCircle /> },
    { id: "programStructure", label: "Structure", icon: <FaLayerGroup /> },
    { id: "eligibilityCriteria", label: "Eligibility", icon: <FaUserGraduate /> },
    { id: "admissionProcess", label: "Admission", icon: <FaClipboardCheck /> },
    { id: "additionalRequirements", label: "Requirements", icon: <FaPlusSquare /> },
  ];

  const renderPoints = (points) => {
    if (!points || points.length === 0) return null;
    return (
      <ul className="custom-portal-list list-unstyled mt-4">
        {points.map((point, index) => (
          <motion.li 
            key={index}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.05 }}
            className="d-flex align-items-start mb-3"
          >
            <div className="bullet-square me-3 mt-2" />
            <span className="text-dark-soft fw-medium">{point}</span>
          </motion.li>
        ))}
      </ul>
    );
  };

  return (
    <div className="portal-master-wrapper">
      <Header />

      {/* Cinematic Graduate Hero */}
      <div className="explorer-hero position-relative overflow-hidden">
        <div className="hero-pattern-overlay"></div>
        <Container className="position-relative" style={{ zIndex: 5 }}>
          <Link to="/programs" className="back-link text-decoration-none mb-4 d-inline-flex align-items-center">
            <FaArrowLeft className="me-2" size={12} /> Graduate Catalog
          </Link>
          
          <Row className="align-items-center">
            <Col lg={8}>
              <div className="d-flex align-items-center gap-3 mb-3">
                <div className="hero-icon-circle shadow-lg"><FaGraduationCap /></div>
                <Badge bg="warning" className="text-dark fw-800 px-3 py-2 rounded-pill shadow-sm">GRADUATE ADMISSIONS</Badge>
              </div>
              <h1 className="hero-title display-4 fw-900 mb-2">{program.title}</h1>
              <p className="hero-subtitle lead opacity-90 mb-4">{program.department}</p>
              
              <div className="d-lg-none">
                <Button 
                  variant="primary" 
                  className="rounded-pill px-4 py-2 nav-hint-pulse fw-bold border-0 shadow"
                  onClick={() => setShowDrawer(true)}
                >
                  <FaFilter className="me-2"/> Program Menu
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Sticky Action Navigation */}
      <nav className="portal-sub-nav sticky-top shadow-sm">
        <Container>
          <div className="d-flex justify-content-between align-items-center py-3">
            <h2 className="h5 fw-900 text-navy mb-0 d-none d-md-block">
              Graduate <span className="text-primary">Portal</span>
            </h2>
            <div className="action-btns d-flex gap-2 flex-grow-1 flex-md-grow-0 justify-content-end">
              <Button 
                href={program.schemaLink} 
                target="_blank"
                variant="outline-primary" 
                className="rounded-pill px-3 px-md-4 fw-bold flex-fill btn-sm-custom"
              >
                <FaDownload className="me-2" /> Curriculum
              </Button>
              <Button 
                href="https://applyadmission.iba-suk.edu.pk/" 
                target="_blank"
                variant="primary" 
                className="rounded-pill px-3 px-md-4 fw-bold flex-fill btn-sm-custom"
              >
                Apply Online <FaExternalLinkAlt size={12} className="ms-2"/>
              </Button>
            </div>
          </div>
        </Container>
      </nav>

      <section className="portal-viewport bg-light-soft py-4 py-lg-5">
        <Container>
          <Row className="g-4">
            {/* Desktop Sidebar */}
            <Col lg={4} className="d-none d-lg-block">
              <div className="sticky-sidebar">
                <div className="sidebar-card border-0 shadow-sm rounded-4 overflow-hidden">
                  <div className="card-label">Graduate Sections</div>
                  <div className="program-list">
                    {navItems.map((item, i) => (
                      <button 
                        key={item.id} 
                        className={`prog-item ${activeSection === item.id ? 'is-active' : ''}`} 
                        onClick={() => handleSectionChange(item.id)}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <div className="icon-circle-mini">{item.icon}</div>
                          <div className="text-start">
                            <div className="name fw-800">{item.label}</div>
                            <small className="stats-tag text-uppercase opacity-75">Grad Section 0{i + 1}</small>
                          </div>
                        </div>
                        <FaChevronRight className="chevron-mini" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </Col>

            {/* Content Viewport */}
            <Col lg={8} ref={contentRef}>
              <AnimatePresence mode="wait">
                <motion.div 
                  key={activeSection} 
                  initial={{ opacity: 0, y: 20 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="details-card bg-white p-4 p-lg-5 rounded-4 shadow-sm border-0"
                >
                  <div className="section-header mb-4 d-flex justify-content-between align-items-start">
                    <div>
                      <span className="category-tag mb-2 d-block">
                        {navItems.find(n => n.id === activeSection)?.label}
                      </span>
                      <h2 className="portal-heading text-navy fw-900 display-6">
                        {program.sections[activeSection]?.heading || navItems.find(n => n.id === activeSection)?.label}
                      </h2>
                      <div className="accent-bar mt-3" />
                    </div>
                    <Badge bg="info" className="bg-opacity-10 text-info px-3 py-2 rounded-pill fw-600 d-none d-md-block">
                      Cycle 2026
                    </Badge>
                  </div>

                  <div className="editorial-text-body">
                    <p className="lead-text mb-4">
                      {program.sections[activeSection]?.content || "Graduate program details are currently being finalized for the upcoming session."}
                    </p>

                    {program.sections[activeSection]?.points && (
                      <div className="highlights-box mt-5">
                        <h5 className="fw-900 text-navy mb-4 d-flex align-items-center">
                          <span className="me-2 text-primary">|</span> Key Requirements & Details
                        </h5>
                        {renderPoints(program.sections[activeSection].points)}
                      </div>
                    )}
                  </div>

                  <div className="content-footer-cta mt-5 p-4 rounded-4 bg-light border d-flex flex-column flex-md-row align-items-center justify-content-between gap-3">
                    <div className="text-center text-md-start">
                      <h6 className="fw-800 text-navy mb-1">Graduate Admissions Help</h6>
                      <p className="small text-muted mb-0">Speak with our graduate program coordinator for specific queries.</p>
                    </div>
                    <Button variant="navy-outline" className="rounded-pill px-4 fw-bold border-2">
                      Get Help
                    </Button>
                  </div>
                </motion.div>
              </AnimatePresence>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Mobile Drawer */}
      <Offcanvas show={showDrawer} onHide={() => setShowDrawer(false)} placement="bottom" className="mobile-drawer rounded-top-5">
        <div className="drawer-handle mx-auto my-3" />
        <Offcanvas.Header closeButton className="px-4">
          <Offcanvas.Title className="fw-900 text-navy">Navigate Graduate Portal</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0">
          <div className="mobile-nav-stack">
            {navItems.map((item, i) => (
              <button 
                key={item.id} 
                className={`mobile-nav-btn ${activeSection === item.id ? 'active' : ''}`}
                onClick={() => { handleSectionChange(item.id); setShowDrawer(false); }}
              >
                <div className="d-flex align-items-center gap-3">
                  <div className="icon-box">{item.icon}</div>
                  <div className="text-start">
                    <div className="fw-bold">{item.label}</div>
                    <small className="opacity-50">Module 0{i + 1}</small>
                  </div>
                </div>
                {activeSection === item.id && <FaChevronRight className="text-primary" />}
              </button>
            ))}
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      <Footer />

      <style>{`
        .portal-master-wrapper { background-color: #f8fafc; font-family: 'Plus Jakarta Sans', sans-serif; }
        .text-navy { color: #001D3D; }
        .fw-900 { font-weight: 900; }
        .fw-800 { font-weight: 800; }
        
        .explorer-hero {
          background: linear-gradient(135deg, #012140 0%, #001D3D 100%);
          padding: 60px 0 80px 0;
          color: white;
          border-bottom: 5px solid #EAB308;
        }
        .hero-pattern-overlay {
          position: absolute; inset: 0;
          background-image: radial-gradient(rgba(234,179,8,0.1) 1px, transparent 1px);
          background-size: 24px 24px;
        }
        .hero-icon-circle {
          width: 50px; height: 50px; background: rgba(255,255,255,0.1);
          border-radius: 12px; display: flex; align-items: center; justify-content: center;
          font-size: 1.5rem; color: #EAB308; border: 1px solid rgba(255,255,255,0.2);
        }
        .back-link { color: rgba(255,255,255,0.6); font-weight: 600; font-size: 0.85rem; transition: 0.3s; }
        .back-link:hover { color: #EAB308; }

        .portal-sub-nav { background: rgba(255,255,255,0.98); backdrop-filter: blur(10px); z-index: 1000; top: 0; border-bottom: 1px solid #e2e8f0; }

        .sticky-sidebar { position: sticky; top: 120px; }
        .sidebar-card { background: white; border: 1px solid #e2e8f0 !important; }
        .card-label { padding: 15px 25px; background: #012140; color: white; font-weight: 800; font-size: 0.7rem; letter-spacing: 1.5px; text-transform: uppercase; }
        
        .prog-item {
          display: flex; align-items: center; justify-content: space-between;
          width: 100%; padding: 18px 25px; border: none; background: transparent;
          border-bottom: 1px solid #f1f5f9; transition: 0.3s ease;
        }
        .prog-item:hover { background: #f8fafc; }
        .prog-item.is-active { background: #012140; border-left: 5px solid #EAB308; }
        .prog-item.is-active .name { color: white; }
        .prog-item.is-active .icon-circle-mini { background: rgba(255,255,255,0.1); color: #EAB308; border-color: transparent; }
        .prog-item.is-active .stats-tag { color: rgba(255,255,255,0.6); }
        .icon-circle-mini { width: 36px; height: 36px; background: #f1f5f9; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: #64748b; border: 1px solid #e2e8f0; }

        .category-tag { font-size: 0.75rem; font-weight: 800; color: #012140; text-transform: uppercase; letter-spacing: 2px; }
        .accent-bar { width: 50px; height: 4px; background: #EAB308; border-radius: 2px; }
        .lead-text { font-size: 1.15rem; line-height: 1.8; color: #334155; }
        .bullet-square { width: 8px; height: 8px; background: #EAB308; border-radius: 2px; flex-shrink: 0; }

        .mobile-drawer { height: 60vh !important; }
        .drawer-handle { width: 40px; height: 5px; background: #e2e8f0; border-radius: 10px; }
        .mobile-nav-btn { display: flex; align-items: center; justify-content: space-between; width: 100%; padding: 15px 25px; border: none; background: white; border-bottom: 1px solid #f1f5f9; }
        .mobile-nav-btn.active { background: #fffcf0; }
        
        .btn-navy-outline { color: #012140; border: 1px solid #012140; transition: 0.3s; }
        .btn-navy-outline:hover { background: #012140; color: white; }

        @media (max-width: 991px) {
          .explorer-hero { padding: 40px 0 50px 0; text-align: center; }
          .hero-icon-circle { margin: 0 auto; }
          .hero-title { font-size: 1.85rem; }
          .btn-sm-custom { padding: 8px 15px; font-size: 0.85rem; }
          .details-card { padding: 25px 20px !important; }
        }

        @keyframes hintPulse {
          0% { box-shadow: 0 0 0 0 rgba(234, 179, 8, 0.4); }
          70% { box-shadow: 0 0 0 10px rgba(234, 179, 8, 0); }
          100% { box-shadow: 0 0 0 0 rgba(234, 179, 8, 0); }
        }
        .nav-hint-pulse { animation: hintPulse 2s infinite; background: #012140 !important; }
      `}</style>
    </div>
  );
};

export default GraduateProgramDetails;